// src/pages/DocumentViewerPage.js
// Updated: 11/02/2025 06:15 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
// --- 1. IMPORT 'functions' and 'httpsCallable' ---
// --- FIX: Corrected the import path from ../../ to ../ ---
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  // --- 2. REMOVED unused imports ---
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Trash2 } from 'lucide-react';

import CommentThread from '../components/CommentThread';

// react-pdf
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Correct worker for react-pdf v6
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

// --- 4. DEFINE THE CALLABLE FUNCTION ---
const adminDeleteDocument = httpsCallable(functions, 'adminDeleteDocument');
// --- 5. REMOVED unused 'deleteGuidanceLinksForDocument' ---

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

export default function DocumentViewerPage() {
  const { docId } = useParams();
  // --- 6. GET 'userData' to pass orgId ---
  const { isAdmin, userData } = useAuth(); // UI gate; Firestore rules still enforce server-side
  const navigate = useNavigate();

  const [documentData, setDocumentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // PDF Viewer State
  const [numPages, setNumPages] = useState(null);
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  // Fetch the document's data from Firestore
  useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      setError('');
      try {
        const docRef = doc(db, 'documents', docId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setDocumentData({ id: snap.id, ...snap.data() });
        } else {
          setError('Document not found.');
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        setError('Failed to load document.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // --- 7. REMOVED unused 'deleteGuidanceLinks' function ---

  // --- 8. REWRITE 'handleDelete' to use the callable function ---
  const handleDelete = async () => {
    if (!documentData || !isAdmin || isDeleting || !userData?.orgId) {
      if (!userData?.orgId) setError("Cannot delete: User organization not loaded.");
      return;
    }

    const prettyName = documentData.title || documentData.fileName || documentData.id;
    if (
      !window.confirm(
        `Are you sure you want to permanently delete the document: "${prettyName}"?\nThis will remove the file from Storage, the Firestore record, and log the action.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      // --- STEP 1: Call the Cloud Function ---
      // Pass only the necessary IDs. The function will verify auth.
      await adminDeleteDocument({ 
        docId: docId,
        orgId: userData.orgId 
      });

      // --- STEP 2: Redirect after successful delete ---
      // The function handled storage, firestore, and logging.
      navigate('/documents');
      
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(`Failed to delete document: ${err.message}`);
      setIsDeleting(false);
    }
  };
  // END DELETE HANDLER

  const renderDocument = () => {
    if (!documentData) return null;

    if (documentData.fileType === 'application/pdf') {
      return (
        <Document
          file={documentData.downloadURL}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={console.error}
          loading={<LoadingSpinner />}
        >
          {Array.from(new Array(numPages), (_el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="mb-4 shadow-md"
              width={500}
            />
          ))}
        </Document>
      );
    }

    if (documentData.fileType === 'image/jpeg' || documentData.fileType === 'image/png') {
      return (
        <div className="overflow-hidden rounded-lg border bg-white">
          <img
            src={documentData.downloadURL}
            alt={documentData.fileName || 'Image'}
            className="w-full object-contain"
          />
        </div>
      );
    }

    return <p>Unsupported file type: {documentData.fileType}</p>;
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <p className="whitespace-pre-line text-red-600">{error}</p>;
  if (!documentData) return <p>Document data not available.</p>;

  return (
    <div>
      <Link
        to="/documents"
        className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Documents
      </Link>

      {/* Document Header and Delete Button */}
      <div className="flex items-start justify-between">
        <h1 className="mb-4 pr-4 truncate text-3xl font-bold text-gray-900">
          {documentData.title || documentData.fileName}
        </h1>

        {/* DELETE BUTTON (ADMIN ONLY) */}
        {isAdmin && (
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex flex-shrink-0 items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
            title="Permanently delete this document and its file from storage."
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
        {/* END DELETE BUTTON */}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:col-span-3 lg:grid-cols-3">
        {/* Main content: Document Viewer */}
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-semibold">Document Preview</h2>
            <div className="h-[80vh] overflow-y-scroll rounded-lg border bg-gray-200 p-4">
              {renderDocument()}
            </div>
          </div>
        </div>

        {/* Sidebar: Comments */}
        <div className="lg:col-span-1">
          <div className="h-[80vh] overflow-y-scroll">
            <CommentThread docId={docId} />
          </div>
        </div>
      </div>
    </div>
  );
}